import React, { useContext, useState, useId, useRef } from 'react';
import { Button } from 'primereact/button';
import { ScrollPanel } from 'primereact/scrollpanel';       
import { ToolsContext, ToolsDispatchContext } from '../../ToolsContext.js';
import { useDeleteGtfsStop, useMutateGtfsStop, queryClient, useQueryGtfsResourceById, useQueryGtfsResource } from '../../Queries.js';
import { OverlayContext } from '../../OverlaysContext.js';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SnapshotContext } from '../../SnapshotContext.js';
import { PlatformIndicator } from '../../components/PlatformIndicator.js';
import { PropertyInput } from '../../components/PropertyInput.js';
import { AccessibilityPropertyCheckbox } from '../../components/AccessibilityPropertyCheckbox.js';
import { PropertyDropdown } from '../../components/PropertyDropdown.js';
import { StopIdPropertyDropdown } from '../../components/StopIdPropertyDropdown.js';
import React from 'react';
import { PropertySelectButton } from '../../components/PropertySelectButton.js';

export function StopPropertyEditor({ selectedItem }) {
    const { isPending, error, data, dataUpdatedAt } = useQueryGtfsResourceById('stops',selectedItem.stop_id);
    const tool = useContext(ToolsContext);
    const dispatch = useContext(ToolsDispatchContext);

    function handleExit() {
        dispatch({
            type: 'changed',
            newTool: {
                ...tool,
                propertyEditor: null,  // wyczyść edytor (spowoduje jego ukrycie)
                selection: null  // odznacz przystanek
            }
        });
        return true;
    }

    if (isPending || !data) {
        return (
            <ProgressSpinner className="flex justify-content-center"/>
        );
    }

    if (error) {
        return (
            <>
                <i className="fa-solid fa-face-frown fa-5x"></i><br />
                Wystąpił błąd ładowania przystanków: {error.message}
            </>
        )
    }


    return (
        <div className='p-3 relative'>
            <Button icon="pi pi-times" rounded text severity="danger" aria-label="Cancel" className='absolute top-0 right-0' onClick={handleExit} />
            <div className='pb-3'><i className="fa-solid fa-pen-to-square mr-2"></i><span className='text-200 text-sm'>{data.stop_id}</span> {data.stop_name} <PlatformIndicator platformCode={data.platform_code} /></div>
            <ScrollPanel style={{width: '100%', height: '80vh'}}>
                <StopPropertiesForm inputValues={data} dataUpdatedAt={dataUpdatedAt}></StopPropertiesForm>
            </ScrollPanel>
        </div>
    )
}

export function StopPropertiesForm({ inputValues, dataUpdatedAt}) {
    const id = useId();
    const modifyStop = useMutateGtfsStop();
    const deleteStop = useDeleteGtfsStop();
    const snap = useContext(SnapshotContext);
    const tool = useContext(ToolsContext);
    const dispatch = useContext(ToolsDispatchContext);
    const [isMutationPending, setIsMutationPending] = useState(false);
    const [modifiedValues, setModifiedValues] = useState(inputValues);
    const [validationErrors, setValidationErrors] = useState({stop_id: false, stop_name: false});
    const locationTypeOptions = [
        { name: 'Przystanek lub stanowisko (peron)', value: 0 },
        { name: 'Stacja lub dworzec', value: 1 },
        { name: 'Wejście lub wyjście ze stacji', value: 2 },
        { name: 'Miejsce odprawy (część peronu)', value: 4 }
    ];
    const {data : stopsList} = useQueryGtfsResource('stops');
    const previousUpdate = useRef(dataUpdatedAt);
    const handleOverlays = useContext(OverlayContext);

    function handleInput(propertyName,value) {
        let newValues = {...modifiedValues};
        newValues[propertyName] = value;
        setModifiedValues(newValues);
    }

    async function handleInputDone(propertyName, value) {
        if(value === inputValues[propertyName]) {
            return; // Nie wysyłamy zmian na serwer, jeżeli nowa wartość jest taka jak już zapisana do serwera.
        }
        const data = {
          query: {
            modified_stop_id: inputValues.stop_id
          },
          fields: {}
        };
        data.fields[propertyName] = value;
        setIsMutationPending(true);

        try {
            // próbujemy przeprowadzić mutację
            const asyncMutation = await modifyStop.mutateAsync(data);

            // dalej wykona się tylko, jeżeli pobranie danych nastąpiło bez błędów

            // wykasuj ewentualne istniejące błędy walidacji z UI, skoro wszystko poszło ok
            if(validationErrors[propertyName]) {
                let updatedValidationErrors = {...validationErrors};
                updatedValidationErrors[propertyName] = false;
                setValidationErrors({
                    ...updatedValidationErrors
                });                   
            }
        } catch(error) {
            const errorCode = error.message.slice(0,3);
            if(errorCode == '422') {
                //obsługa błędów walidacji
                let updatedValidationErrors = {...validationErrors};
                updatedValidationErrors[propertyName] = Object.values(error.cause.validation[propertyName]).join(', ');
                setValidationErrors({
                    ...updatedValidationErrors
                });
            } else {
                //obsługa pozostałych błędów
                handleOverlays('toast',{
                    severity: "error",
                    summary: 'Błąd ' + errorCode,
                    detail: (typeof error.cause === 'undefined' || typeof error.cause.message === 'undefined') ? 'Wystąpił błąd. Czynność nie mogła zostać zakończona powodzeniem. ' + error.message : error.cause.message
                });
            }

        } finally {
            setIsMutationPending(false);
        }

    }

    function handleInputCancel() {
        setModifiedValues(inputValues);
    }

    function handleClickDelete() {
        async function accept() {
            try {
                //próbujemy usunąć przystanek
                const asyncMutation = await deleteStop.mutateAsync(inputValues.stop_id);
                // to wykona się tylko, jeżeli nam się udało
                dispatch({ // zamykamy edytor koniecznie
                    type: 'changed',
                    newTool: {
                        ...tool,
                        propertyEditor: null,  // wyczyść edytor (spowoduje jego ukrycie)
                        selection: null  // odznacz przystanek
                    }
                });
                handleOverlays('toast',{ summary: 'Przystanek został usunięty', severity: 'success'});
            } catch(error) { 
                // Obługa błędów
                console.log(error);
                const errorCode = error.message.slice(0,3);
                handleOverlays('toast',{
                    severity: "error",
                    summary: 'Błąd ' + errorCode,
                    detail: (typeof error.cause === 'undefined' || typeof error.cause.message === 'undefined') ? 'Wystąpił błąd. Czynność nie mogła zostać zakończona powodzeniem.' : error.cause.message 
                });               
            } finally {
                // musimy być pewni, że inwalidacja zapytań nastąpi PO zakończeniu operacji
                // dlatego następuje tutaj, a nie w Queries.js
                queryClient.invalidateQueries({queryKey: ['GTFS', snap, 'stops', inputValues.stop_id]});
                queryClient.invalidateQueries({queryKey: ['stopsData']});
                queryClient.invalidateQueries({queryKey: ['stops', snap]});
            }
        }

        handleOverlays('confirmDialog',{
            message: 'Przystanek zniknie z rozkładu jazdy wszystkich linii. Operacja jest nieodwracalna.',
            header: 'Usunąć przystanek ' + inputValues.stop_id + ' ' + inputValues.stop_name + '?',
            icon: 'pi pi-info-circle',
            style: { width: '500px '},
            breakpoints: { '960px': '100vw' },
            defaultFocus: 'reject',
            acceptLabel: 'Usuń',
            acceptClassName: 'p-button-danger',
            acceptIcon: 'fa-solid fa-trash',
            rejectLabel: 'Anuluj',
            accept
        })
    }

    if( dataUpdatedAt > previousUpdate.current ) {
        previousUpdate.current = dataUpdatedAt;
        setModifiedValues(inputValues);
    }

    return (
        <div className="formgrid grid">
            <div className="field col-6">
                <PropertyInput
                    propertyName='stop_id'
                    label="ID przystanku"
                    id={id}
                    value={modifiedValues.stop_id}
                    initialVal={inputValues.stop_id}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    invalid={validationErrors.stop_id}
                    disabled={false}
                    pending={isMutationPending}
                /* onClick={ (e) => { setModifiedValues({...inputValues}) }}  */
                />
            </div>
            <div className="field col-6">
                <PropertyInput
                    propertyName='stop_code'
                    label="Publiczny nr przyst."
                    id={id}
                    value={modifiedValues.stop_code}
                    initialVal={inputValues.stop_code}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-8">
                <PropertyInput
                    propertyName='stop_name'
                    label="Nazwa przystanku"
                    id={id}
                    value={modifiedValues.stop_name}
                    initialVal={inputValues.stop_name}
                    onChange={handleInput}
                    onDone={handleInputDone}
                    onCancel={handleInputCancel}
                    invalid={validationErrors.stop_name}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-4">
                <PropertyInput
                    propertyName='platform_code'
                    label="Nr stan."
                    id={id}
                    value={modifiedValues.platform_code}
                    initialVal={inputValues.platform_code}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-12">
                <PropertyInput
                    propertyName='tts_stop_name'
                    label="Nazwa przystanku dla lektora"
                    id={id}
                    value={modifiedValues.tts_stop_name}
                    initialVal={inputValues.tts_stop_name}
                    placeholder={inputValues.stop_name}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-6">
                <PropertyInput numeric={true}
                    propertyName='stop_lat'
                    initialVal={inputValues.stop_lat}
                    label="Szer. geo. słupka"
                    id={id}
                    value={modifiedValues.stop_lat}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-6">
                <PropertyInput numeric={true}
                    propertyName='stop_lon'
                    initialVal={inputValues.stop_lon}
                    label="Długość geo. słupka"
                    id={id}
                    value={modifiedValues.stop_lon}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-4">
                <PropertyInput
                    propertyName='zone_id'
                    initialVal={inputValues.zone_id}
                    label="Strefa taryfowa"
                    id={id}
                    value={modifiedValues.zone_id}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
            <div className="field col-8">
                <PropertyInput
                    propertyName='stop_url'
                    initialVal={inputValues.stop_url}
                    label="Link do informacji dla pasażerów"
                    value={modifiedValues.stop_url}
                    id={id}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                />
            </div>
{/*             <div className="field col-12 flex align-content-center">
                <AccessibilityPropertyCheckbox 
                    propertyName="wheelchair_boarding" 
                    value={inputValues.wheelchair_boarding} 
                    id={id} 
                    onDone={handleInputDone} 
                />
            </div> */}
            <PropertySelectButton
                id={id}
                propertyName='wheelchair_boarding'
                initialVal={inputValues.wheelchair_boarding}
                label='Dostępność dla wózków:'
                options={[
                    { label: 'Brak info', value: 0 },
                    { label: 'Dostępny', value: 1 },
                    { label: 'Niedostępny', value: 2 }
                ]}
                optionValue='value'
                onDone={handleInputDone}
            />
            <div className='field col-12 mt-3'>
                <PropertyDropdown 
                    propertyName='location_type' 
                    initialVal={inputValues.location_type} 
                    label="Charakter przystanku" 
                    options={locationTypeOptions} 
                    onDone={handleInputDone} 
                />
            </div>
            {
                // Todo: zrobić filtrowanie opcji dla kompotentu StopIdPropertyDropdown
                // tak, żeby w razie location_type=0, 2 lub 3 w opcjach znajodowały się
                // tylko przystanki o location_type=1 (stacje), a w przypadku location_type=4
                // znajdowały się przystanku location_type=0 (perony)

                (inputValues.location_type !== 1) ?
                    <div className='field col-12'>
                        <StopIdPropertyDropdown propertyName='parent_station' initialVal={inputValues.parent_station} label="Przystanek nadrzędny" options={Object.values(stopsList)} onDone={handleInputDone} />
                    </div>
                    :
                    <></>
            }
            <div className="field col-12 mt-5">
                <Button severity="danger" onClick={handleClickDelete} icon="fa-solid fa-trash" label="Usuń przystanek" outlined></Button>
            </div>
        </div>
    );
}